import { useState } from "react";
import { redirectTo } from "common/utils/browser";
import { notifyError } from "common/utils/app-monitoring/notify";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import {
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  isSessionFinished,
  pinningSessionsAtom,
  type DesignLabPlacementKey
} from "@design-lab/entities/pinning";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useAtomValue } from "jotai";
import { infoPath } from "@design-lab/shared/lib/formPathHelpers";
import { useEditCartMutation } from "../api/useEditCartMutation";

const placements = ["front", "frontLeftChest", "back"] as const;

export function useEditCart() {
  const { mutateAsync: editCart, status, isPending } = useEditCartMutation();
  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const { assemblyGroupId } = useEditMode();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const pinningSessions = useAtomValue(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);

  const handleEditCart = async () => {
    if (!assemblyGroupId) {
      console.error("No assembly group ID found for editing");
      return;
    }

    const artworkData = artworkForm.getValues();
    const compositionData = compositionForm.getValues();

    // Prepare data similar to add to cart
    const variants: Record<string, { quantity: number }> = {};
    const placementsData: Array<any> = [];

    // Process variants
    compositionData.addedCompositions.forEach(composition => {
      Object.entries(composition.variants).forEach(([variantId, quantity]) => {
        const quantityNum = typeof quantity === "number" ? quantity : 0;
        if (quantityNum <= 0) {
          return;
        }

        variants[variantId] = {
          quantity: quantityNum
        };
      });
    });

    // Process placements
    const placementsToEdit = placements.filter(
      placement => placement === "back" || artworkData.selectedFrontPlacement === placement
    );

    placementsToEdit.forEach(placement => {
      const placementData = artworkData[placement];

      if (placementData && placementData.artworkId && placementData.size) {
        const placementForBackend = placement === "frontLeftChest" ? "front_left_chest" : placement;
        const infoKey = placement === "back" ? "back" : "front";
        const placementInfo = artworkData.info[infoKey];

        // Ensure enhancementRequestNote and editImageManual are initialized
        // This prevents them from being undefined if the placement form was never opened
        const enhancementRequestNote = placementInfo?.enhancementRequestNote ?? "";
        const editImageManual = placementInfo?.editImageManual ?? false;

        // Initialize in form if not already set
        if (placementInfo?.enhancementRequestNote === undefined) {
          artworkForm.setValue(infoPath(infoKey, "enhancementRequestNote"), "");
        }
        if (placementInfo?.editImageManual === undefined) {
          artworkForm.setValue(infoPath(infoKey, "editImageManual"), false);
        }

        const placementKey = placement as DesignLabPlacementKey;
        const { session } = getPinningSessionForPlacement({
          placementKey,
          designProjectPlacementIds,
          pinningSessions,
          artworkId: placementData.artworkId ? String(placementData.artworkId) : undefined
        });
        const pinningSessionId = isSessionFinished(session?.state) ? session!.sessionId : undefined;

        placementsData.push({
          dtfArtwork: {
            id: Number(placementData.artworkId),
            enhancementTier: placementInfo.enhancementTier,
            removeBackground: placementInfo.removeBackground,
            editImageManual,
            enhancementRequestNote,
            jiffyDesignServices: placementInfo.jiffyDesignServices,
            pinningSessionId
          },
          width: placementData.size.width,
          height: placementData.size.height,
          position: placementForBackend
        });
      }
    });

    try {
      await editCart({
        assemblyGroupId,
        variants,
        placements: placementsData
      });

      setIsRedirecting(true);
      redirectTo("/cart");
    } catch (error) {
      console.error("Failed to edit cart item:", error);
      notifyError("Failed to edit cart item", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          hook: "useEditCart",
          assemblyGroupId
        }
      });
      setIsRedirecting(false);
      throw error;
    }
  };

  return {
    handleEditCart,
    isEditingCart: isPending || isRedirecting,
    status
  };
}
